import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import {
  useAddPanFormMutation,
  useSavePanFormImagesMutation,
} from "src/services/PANService";
import { showToast } from "src/utils/toaster/showToast";
import { panFormToCertificate } from "./panFormToCertificate";
import PanFormTemplate from "./PanFormTemplate";
import PanFormStepOne from "./PanFormStepOne";
import {
  AADHAAR_NAME_MAX,
  ACTION_BAR_HEIGHT,
  ATTACHMENT_LAYOUTS,
  AttachmentLayout,
  emptyPanFormValues,
  IncomeSource,
  PanFormAttachment,
  PanFormAttachmentPage,
  PanFormValues,
  slotsForLayout,
} from "./panFormFields";
import { INCOME_SOURCE_CODES, STATE_OPTIONS } from "./panFormMasters";
import { cleanSignatureImage } from "./signatureInk";
import { extractReceiptImage } from "./receiptCrop";
import CrossSignControls from "./CrossSignControls";
import {
  useAddEmployerCertificateMutation,
  useLazyGetemployerCertificateDoneApplicationByIdQuery,
} from "src/services/EmployerCertificateService";
import { employerCertificateHtml } from "src/pages/employer-certificate/employerCertificateHtml";
import { pcoFirmFor } from "src/pages/employer-certificate/pcoFirms";
import { useLazyGetPcoFirmsQuery } from "src/services/PcoFirmService";
import { useEnsureDsaSignatureMutation } from "src/services/DsaIssuerService";
import {
  isDsaCertificate,
  cleanDsaStamp,
  resolveDsaSignature,
} from "src/pages/employer-certificate/certificateIssuer";
import { isEmployerCertificateCategory } from "src/pages/employer-certificate/employerCertificateCategories";
import { ackFileBaseName, fileExtensionFromUrl } from "src/utils/downloadFileAs";
import { affidavitHtml } from "src/pages/affidavit-certificate/AffidavitTemplate";
import {
  AffidavitStampSettings,
  affidavitMarks,
  isNotaryOn,
  loadAffidavitStamps,
  urlToDataUrl,
} from "src/pages/affidavit-certificate/affidavitStamps";
import { useAddAffidavitCertificateMutation } from "src/services/AffidavitCertificateService";
import {
  useLazyGetAffidavitStampQuery,
  useReserveAffidavitSerialMutation,
} from "src/services/AffidavitStampService";

type Props = {
  applicationData: any;
  /**
   * The remark this application already carries, resolved by the page that
   * opens this form — from the application itself where the API stores it, or
   * from the saved form's `formData` where an older API build left it.
   */
  savedRemark?: string;
  onClose: () => void;
  onGenerated?: (fileUrl: string) => void;
  /**
   * REFILL PAN FORM: the `formData` the last generate saved. When given, the
   * form opens with those values over the usual application prefill, so the
   * operator corrects the generated form instead of filling it again.
   */
  savedFormData?: Record<string, any> | null;
  /**
   * REFILL PAN FORM: the form type picked in the Refill dialog (Form 49A or
   * Correction). Only the PDF is generated in this type — the application
   * record keeps its own type. Unset = the application's type.
   */
  applicationTypeOverride?: "NEW" | "CORRECTION" | null;
  /** The type `savedFormData` was generated as, to tell a type switch apart. */
  savedApplicationType?: string | null;
};

/**
 * Saved values that only make sense on the form type they were typed on — the
 * address block (49A splits residence / office, the correction form has one
 * block), the address ticks, income source, place, and the correction-only
 * change / proof boxes. When a refill switches the type, these come from the
 * new type's own prefill instead of the saved form.
 */
const TYPE_SPECIFIC_KEYS = new Set<string>([
  "flat", "road", "postOffice", "area", "district", "state", "country", "pincode",
  "stateCode", "countryCode",
  "officeFlat", "officeRoad", "officePostOffice", "officeArea", "officeDistrict",
  "officeState", "officeCountry", "officePincode", "officeStateCode", "officeCountryCode",
  "addressForComm", "addressType", "incomeSources", "place", "pan",
  "proofOfIdentity", "proofOfAddress", "proofOfDob",
  "corrProofOfIdentity", "corrProofOfAddress", "corrProofOfDob", "corrDocProof", "corrCopyOfPan",
  "changeName", "changeGender", "changeDob", "changeAddress", "changePassport",
  "changeTin", "changeContact", "changeFather", "changeMother",
]);

// Split a full name into first / middle / last.
//
// A single-word name goes into LAST name, not first: the last-name box is the
// mandatory one on both forms (NSDL treats it as the surname), so putting a
// one-word name in "First" left the mandatory box empty and the operator had to
// move it by hand every time. Applies to the applicant and to both parents.
const splitName = (full?: string) => {
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", middle: "", last: "" };
  if (parts.length === 1) return { first: "", middle: "", last: parts[0] };
  if (parts.length === 2) return { first: parts[0], middle: "", last: parts[1] };
  return {
    first: parts[0],
    middle: parts.slice(1, -1).join(" "),
    last: parts[parts.length - 1],
  };
};

const onlyDigits = (s?: string) => (s || "").replace(/\D/g, "");

/** The form's "Name of Verifier" box is char(75). */
const DECLARANT_NAME_MAX = 75;

/**
 * First / Middle / Last put back together as one line, the way the verifier's
 * name is printed and sent to the vendor — one box, not three.
 */
const joinName = (first?: string, middle?: string, last?: string) =>
  [first, middle, last]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ")
    .slice(0, DECLARANT_NAME_MAX);


/**
 * A name reduced to what a comparison should care about.
 *
 * Case, run-together spaces and the punctuation people put in names (full
 * stops after an initial, hyphens, commas) are not differences a counter would
 * call a mismatch, so they are taken out before the two names are compared.
 * Anything left that differs IS a different name.
 */
const nameKey = (value?: string) =>
  (value || "")
    .toUpperCase()
    .replace(/[.,'-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * What "Name as per Aadhaar" may read, given the Full Name boxes.
 *
 * TWO forms are accepted — the whole name, and the name without the first box —
 * because the first box does not mean the same thing on every application.
 *
 * On some records the name is written in the local order with the family name
 * first: "PARGI ALKESHBHAI RAMABHAI" is Alkeshbhai, son of Ramabhai, of the
 * Pargi family, and the Aadhaar card prints "ALKESHBHAI RAMABHAI" without that
 * leading family name. On others the first box is simply the given name —
 * "MOHAMMAD YUNUS JOYE" is printed on the card exactly as it stands, and
 * demanding "YUNUS JOYE" there refuses the correct value.
 *
 * Nothing on the record says which kind of name it is holding, so both are
 * allowed and the operator types what the card in their hand actually says.
 * That still catches what this check is for: a misspelling, a dropped word, a
 * different name altogether.
 */
const acceptedAadhaarNames = (first?: string, middle?: string, last?: string) => {
  const join = (parts: (string | undefined)[]) =>
    parts
      .map((p) => (p || "").trim())
      .filter(Boolean)
      .join(" ")
      .slice(0, AADHAAR_NAME_MAX);
  const whole = join([first, middle, last]);
  const withoutFirst = join([middle, last]);
  // De-duplicated, so a record with no first box offers one form, not two
  // identical ones in the error message.
  return Array.from(new Set([whole, withoutFirst].filter(Boolean)));
};

/**
 * §20 and §21 of the Representative Assessee block, seeded from the record.
 *
 * A minor's application is filed BY the guardian: the mobile number and e-mail
 * on the record are the ones the guardian gave when they submitted it, so they
 * are the RA's own contact details, not a second person's. The RA is an Indian
 * guardian on every application this panel files, so the dialling code and the
 * country are the same constants as the applicant's own block.
 *
 * Handed out from here rather than written into the prefill twice, because §22
 * can also be moved ONTO Representative Assessee by hand and an operator who
 * does that wants the same head start a minor's form opens with.
 */
const raContactFrom = (applicationData: any): Partial<PanFormValues> => ({
  raMobileCC: "91",
  raMobile: onlyDigits(applicationData?.mobileNumber).slice(-10),
  raEmail: applicationData?.email || "",
  raCountry: "INDIA",
  raCountryCode: "IN",
});

const asDate = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format("DD/MM/YYYY") : "";

/**
 * Fields the official spec marks Mandatory, checked before the operator can move
 * on to the preview. Keys map 1:1 to the step-1 inputs, so a failure can be
 * outlined in place.
 */
const requiredKeys = (
  isNew: boolean,
  addressForComm: string
): (keyof PanFormValues)[] => [
  "applicantStatus",
  "countryOfCitizenship",
  "lastName",
  "aadhaarName",
  "gender",
  "dob",
  // Only the first two address lines are mandatory — "postOffice" and "area"
  // are optional (see AddressRows in PanFormStepOne).
  "flat",
  "road",
  "district",
  "stateCode",
  "pincode",
  "mobileCountryCode",
  "mobile",
  "email",
  "fatherLast",
  "motherLast",
  "printParent",
  "singleParent",
  "poiCode",
  "poaCode",
  "podbCode",
  "declarantName",
  "verifierCapacityCode",
  "place",
  "date",
  "dateOfReceipt",
  "dateOfVerification",
  "photoPresent",
  "signaturePresent",
  ...(isNew
    ? ([
        // Source of income is a Form 49A item only — the correction form has no
        // tick for it, so it is neither shown nor demanded there.
        "incomeSources",
        "residentialStatus",
        "addressForComm",
        // Part D (AO Code) is printed on Form 49A / Form No. 93 only.
        "aoAreaCode",
        "aoType",
        "aoRangeCode",
        "aoNo",
      ] as (keyof PanFormValues)[])
    : (["pan", "addressType"] as (keyof PanFormValues)[])),
  // The office and RA blocks are only on screen when they were picked as the
  // address for communication, so they are demanded exactly then — a key that
  // has no visible input would fail validation with nothing to focus.
  ...(isNew && addressForComm === "OFFICE"
    ? ([
        "officeFlat",
        "officeRoad",
        "officeDistrict",
        "officeStateCode",
        "officePincode",
      ] as (keyof PanFormValues)[])
    : []),
  ...(isNew && addressForComm === "RA"
    ? ([
        "raLast",
        "raFlat",
        "raRoad",
        "raDistrict",
        "raStateCode",
        "raPincode",
        /*
          The RA's Aadhaar and contact details. A minor is filed through the
          representative assessee and the department writes to them, not to the
          applicant — so the RA's own identity and contact carry the same weight
          here as the applicant's `aadhaar` / `mobile` / `email` do above, and
          are demanded exactly when the RA block is on screen (i.e. the minor
          case, where `addressForComm` opens on RA).
        */
        "raAadhaar",
        "raMobile",
        "raEmail",
        // The RA's own proofs (§24) are printed alongside the RA block, so they
        // are demanded exactly when that block is on screen.
        "raPoiCode",
        "raPoaCode",
      ] as (keyof PanFormValues)[])
    : []),
];

/**
 * Fields that are mandatory *and* have a fixed width: being non-empty is not
 * enough, the operator has to fill every box. An Indian PIN is exactly six
 * digits — a short one printed into the six-box grid is what the department
 * rejects the form for, so it is caught here rather than at the counter.
 */
const PIN_LEN = 6;

const fixedLengthKeys: Partial<Record<keyof PanFormValues, number>> = {
  pincode: PIN_LEN,
  officePincode: PIN_LEN,
  raPincode: PIN_LEN,
};

/** The form's address boxes are char(25); a prefill has to respect that too. */
const ADDRESS_LINE_LEN = 25;

const clampLine = (value: string) => value.slice(0, ADDRESS_LINE_LEN);

/** How many 25-box rows the address block has before District. */
const ADDRESS_ROWS = 4;

/**
 * Pour an address into the form's fixed-width rows WITHOUT losing its tail.
 *
 * Each row is 25 boxes, so a long line — "C/O SHREE RAJ JAN SEVA KENDRA", a
 * counter with a long firm name — used to be sliced to fit and the rest of it
 * simply never reached the form. It is wrapped onto the following row instead:
 * the department reads the four rows as one address, so a word carried over
 * prints the same address, while a sliced one prints a different (wrong) one.
 *
 * An address whose every part already fits keeps its part-per-row layout, so
 * the common case reads exactly as before — firm, premises, area, city, each on
 * its own row. Only an overflowing one is re-flowed, greedily, in order.
 *
 * Content past the last row is still dropped: there are only four rows on the
 * form. That now takes more than 100 characters before District.
 */
const fitAddressLines = (parts: string[], rows = ADDRESS_ROWS) => {
  const filled = parts.map((part) => part.trim()).filter(Boolean);

  // Already fits as-is — keep one part per row.
  if (
    filled.length <= rows &&
    filled.every((part) => part.length <= ADDRESS_LINE_LEN)
  ) {
    return filled;
  }

  // Split into words, breaking any single word too long for one row.
  const words = filled
    .join(" ")
    .split(/\s+/)
    .flatMap((word) => {
      if (word.length <= ADDRESS_LINE_LEN) return [word];
      const chunks: string[] = [];
      for (let at = 0; at < word.length; at += ADDRESS_LINE_LEN) {
        chunks.push(word.slice(at, at + ADDRESS_LINE_LEN));
      }
      return chunks;
    });

  const lines: string[] = [];
  words.forEach((word) => {
    const current = lines.length ? lines[lines.length - 1] : "";
    if (current && `${current} ${word}`.length <= ADDRESS_LINE_LEN) {
      lines[lines.length - 1] = `${current} ${word}`;
    } else {
      lines.push(word);
    }
  });

  return lines.slice(0, rows);
};

/** The address lines the correction form's single address block holds. */
type CounterAddressParts = {
  flat: string;
  road: string;
  postOffice: string;
  area: string;
  district: string;
  state: string;
  stateCode: string;
  pincode: string;
};

// The five PCO firm names and the SRN → firm pick live in pcoFirms.ts, shared
// with the Employer Certificate (which prints the firm's issuer + seal).

/** Everything under the firm name — the same head office on every PCO form. */
const PCO_OFFICE_ADDRESS = {
  road: "C/O JAIN BANDHU",
  postOffice: "BCM CITY",
  area: "NAVLAKHA SQ",
  district: "INDORE",
  state: "MADHYA PRADESH",
  stateCode: "18",
  pincode: "452001",
};

const PanFormDialog = ({
  applicationData,
  savedRemark = "",
  onClose,
  onGenerated,
  savedFormData = null,
  applicationTypeOverride = null,
  savedApplicationType = null,
}: Props) => {
  const isRefill = !!savedFormData;
  const [isGenerating, setIsGenerating] = useState(false);
  // Two-step flow: 1 = fill in every field and arrange attachments, 2 = preview
  // exactly what will print, then generate. The preview must stay mounted on
  // step 2 because generatePDF() reads #pdfContent out of the DOM.
  const [step, setStep] = useState<1 | 2>(1);
  const [values, setValues] = useState<PanFormValues>(emptyPanFormValues);
  const [isInitialized, setIsInitialized] = useState(false);
  // Documents already on the application, available to append as extra A4
  // pages. Nothing is selected by default — the operator opts in.
  const [attachments, setAttachments] = useState<PanFormAttachment[]>([]);
  // The appended pages themselves. Each holds one to four selected documents.
  const [attachmentPages, setAttachmentPages] = useState<PanFormAttachmentPage[]>([]);
  const pageSeq = useRef(0);
  const [invalidKeys, setInvalidKeys] = useState<string[]>([]);
  /**
   * The form filler's note to whoever generates and prints this form.
   *
   * Its own state rather than a key in `values`: nothing here is printed. It is
   * a hand-off between two operators — a document that looked doubtful, a name
   * read off an unclear scan, a correction made by hand — and it shows in red
   * on the PAN Details page for the person who picks the application up next.
   * Keeping it out of `values` is what guarantees it can never reach the form
   * overlay or the vendor upload file, and it is optional, so it also stays
   * clear of the required-key lists.
   *
   * Seeded in the prefill effect below from whatever the last generate saved,
   * so re-generating a form shows the note already on the record instead of
   * silently dropping it.
   */
  const [formFillRemark, setFormFillRemark] = useState("");
  /**
   * The acknowledgement receipt cut out of `acknowledgementPdf`, printed as the
   * first page while `values.receiptPage` is "YES". Its own state, not a key in
   * `values`, so the image is never persisted with formData.
   */
  const [receiptImage, setReceiptImage] = useState<string | null>(null);
  const [receiptStatus, setReceiptStatus] = useState<
    "loading" | "ready" | "none" | "error"
  >("loading");

  /**
   * The DSA counter address, kept so the correction form's single address
   * block can be filled and emptied as the Residence / Office tick moves. Null
   * on every other application — there is no counter address to offer.
   */
  const counterPartsRef = useRef<CounterAddressParts | null>(null);
  /** True while that block holds the counter address rather than typed text. */
  const counterPrefilledRef = useRef(false);

  const [addPanForm] = useAddPanFormMutation();
  const [savePanFormImages] = useSavePanFormImagesMutation();
  const [fetchEmployerApplication] =
    useLazyGetemployerCertificateDoneApplicationByIdQuery();
  const [fetchPcoFirms] = useLazyGetPcoFirmsQuery();
  const [addEmployerCertificate] = useAddEmployerCertificateMutation();
  const [ensureDsaSignature] = useEnsureDsaSignatureMutation();
  const [addAffidavitCertificate] = useAddAffidavitCertificateMutation();
  const [fetchAffidavitStamps] = useLazyGetAffidavitStampQuery();
  const [reserveAffidavitSerial] = useReserveAffidavitSerialMutation();
  /** A certificate warning was shown — hold the window open to read it. */
  const certificateWarnedRef = useRef(false);
  /** The photo / signature pair last saved, so an unchanged pair is not re-sent. */
  const lastSavedImagesRef = useRef<{
    photo: string | undefined;
    signature: string | undefined;
  } | null>(
    null
  );

  const applicationType: "NEW" | "CORRECTION" =
    applicationTypeOverride ||
    (applicationData?.applicationType === "CORRECTION" ? "CORRECTION" : "NEW");
  /** A refill generating the form as the other type than it was saved as. */
  const isTypeSwitched =
    isRefill &&
    !!savedApplicationType &&
    savedApplicationType !== applicationType;
  const isNew = applicationType === "NEW";

  /**
   * Which of the two fields carries the category depends on how the
   * application was priced: `baseCatAppied` is set when DSA or PCO was the
   * base category (pan.helper.ts), while `paymentCategory` lists every
   * category paid for. Entries are `{ code }` objects on current records and
   * were bare strings on older ones, so both shapes are matched.
   */
  const hasCategory = (code: string) =>
    applicationData?.baseCatAppied === code ||
    (Array.isArray(applicationData?.paymentCategory)
      ? applicationData.paymentCategory.some((item: any) =>
          typeof item === "string" ? item === code : item?.code === code
        )
      : false);

  /**
   * DSA and PCO applications are both filed over a counter, so the department
   * writes to the counter rather than to the applicant's home: their forms open
   * on Office Address instead of Residence (NEW §22, and the correction form's
   * one Residence / Office tick).
   */
  const isOfficeAddressCategory =
    hasCategory("CAT_DSA") || hasCategory("CAT_PCO");

  /**
   * DSA puts the retailer's / distributor's own counter in that block,
   * prefilled from their profile below.
   */
  const isCounterCategory = hasCategory("CAT_DSA");

  /**
   * PCO puts the head office there instead — a fixed address (PCO_OFFICE_ADDRESS)
   * under one of five firm names, the same for every PCO application.
   *
   * It reads nothing off the retailer's profile: a PCO application is not filed
   * at the counter that submitted it, which is why prefilling that counter's
   * premises here was taken out. DSA wins where an application paid for both,
   * because there the counter address is the real one.
   */
  const isPcoOfficeCategory = !isCounterCategory && hasCategory("CAT_PCO");

  useEffect(() => {
    if (!applicationData || isInitialized) return;

    const name = splitName(applicationData?.name);

    // Parents: prefer explicit father/mother, else single-parent parentName
    const fatherSource =
      applicationData?.fatherName ||
      (applicationData?.parentType === "FATHER" ? applicationData?.parentName : "");
    const motherSource =
      applicationData?.motherName ||
      (applicationData?.parentType === "MOTHER" ? applicationData?.parentName : "");
    const father = splitName(fatherSource);
    const mother = splitName(motherSource);

    const dobDigits = applicationData?.dob
      ? moment(applicationData.dob).isValid()
        ? moment(applicationData.dob).format("DDMMYYYY")
        : onlyDigits(applicationData.dob)
      : "";

    /*
      A minor cannot hold the PAN in their own right: the application is made
      through a representative assessee, and the department writes to that
      representative — so §22 opens on Representative Assessee Address rather
      than on the residence, or on the counter for a DSA / PCO application.

      Age is measured the way the API measures it (basicValidation.isMinor):
      under 18 as of today. The record stores the date either as a parseable
      date or as bare DDMMYYYY digits, and `dobDigits` has already normalised
      both, so the age is read back off that.
    */
    const dobMoment = dobDigits
      ? moment(dobDigits, "DDMMYYYY", true)
      : null;
    const isMinorApplicant =
      !!dobMoment && dobMoment.isValid() && moment().diff(dobMoment, "years") < 18;

    /*
      Whether §22 opens on Representative Assessee — the one condition that
      makes the RA block real, both on screen (PanFormStepOne shows it on
      `isNew && addressForComm === "RA"`) and on paper. Only the NEW form
      prints an RA block at all, so a correction is never one.
    */
    const isRaCommunication = isNew && isMinorApplicant;

    // The residence address is deliberately left BLANK for the operator to type.
    //
    // It used to be parsed from `appliedByUserCurrentAddress`, but that field is
    // the address of the retailer/distributor who submitted the application, not
    // the applicant's — every application filed by the same retailer carried an
    // identical address. The PAN record has no applicant address field at all,
    // so prefilling here printed the wrong person's address on the form.

    const today = moment().format("DD/MM/YYYY");

    /*
      The date the panel took the application in hand. It reads the
      verification date, not the applied date: the panel receives and verifies
      an application in the same sitting, and a receipt date earlier than the
      verification date only invites the vendor to query it. Falls back to the
      applied date, then today.

      Both Date of Receipt and the Declaration Date printed at the foot of the
      form take this. The declarant signs the form on the day the panel handles
      it, so a declaration dated later than the receipt — which is what today's
      date gave on every application opened after the day it came in — reads as
      a form signed after it was filed.
    */
    const receiptDate =
      asDate(applicationData?.verifiedOnDate) ||
      asDate(applicationData?.appliedOnDate) ||
      today;

    /*
      The office address block, filled two different ways.

      DSA takes the counter of the retailer or distributor who filed the
      application, off their profile (the `appliedByUser*` parts, added to
      /pan-app/admin/view for exactly this). PCO takes the fixed head office
      instead, which reads nothing off the application at all.

      Only those two get it. On every other application the office block is the
      applicant's own employer, and filling it with a counter address would
      print the wrong premises — the same reason the residence block above is
      deliberately left blank.
    */
    const counterAddress: Partial<PanFormValues> = {};
    if (isCounterCategory) {
      const part = (value: any) =>
        typeof value === "string" ? value.trim().toUpperCase() : "";

      /*
        The counter address, line by line — with the joined string as a
        fallback.

        /pan-app/admin/view computes each line on its own
        (`appliedByUserAddressLine` and friends), but every admin read is
        projected through the role's access-field list
        (auth.helper.ts `projectQuery`), and those part fields only survive it
        once seed-pan-counter-address-access.js has been run against the
        database. Where it has not, they arrive `undefined` and every box below
        the care-of line came out blank — while the view screen still showed
        the address, because the older joined string
        `appliedByUserCurrentAddress` has always been on that list. That is the
        one field guaranteed to be here, so it fills in for the rest.

        Its shape is fixed by the same aggregation that builds it:

          address, area, cityVillageName, district pincode, state

        and it is read from the END, so an address line carrying commas of its
        own stays whole instead of shifting every later part one place left.
      */
      const joinedParts = part(applicationData?.appliedByUserCurrentAddress)
        .split(",")
        .map((piece) => piece.trim());
      const joined =
        joinedParts.length >= 5
          ? {
              addressLine: joinedParts.slice(0, -4).join(", "),
              area: joinedParts[joinedParts.length - 4],
              city: joinedParts[joinedParts.length - 3],
              // District and PIN share the second-to-last segment.
              districtAndPin: joinedParts[joinedParts.length - 2],
              state: joinedParts[joinedParts.length - 1],
            }
          : null;
      const joinedPincode =
        (joined?.districtAndPin.match(/\b[1-9]\d{5}\b/) || [])[0] || "";
      const joinedDistrict = joined
        ? joined.districtAndPin.replace(/\b[1-9]\d{5}\b/, "").trim()
        : "";

      const city =
        part(applicationData?.appliedByUserCityVillageName) ||
        joined?.city ||
        "";
      const firm = part(applicationData?.appliedByUserFirmName);
      const addressLine =
        part(applicationData?.appliedByUserAddressLine) ||
        joined?.addressLine ||
        "";
      const stateName =
        part(applicationData?.appliedByUserState) || joined?.state || "";
      const stateHit = STATE_OPTIONS.find((o) => o.name === stateName);

      /*
        Some counters are on the books with the whole address in one string and
        no separate PIN, which leaves a required box the operator cannot fill.
        Every current DSA counter carries its own PIN, so this is a guard
        rather than a fix for anything live.

        The PIN is the one part that can be recovered from an address line
        safely: an Indian PIN is six digits, never starts with a zero, and is
        written last, so the final token of that shape is the code. District
        already falls back to the city / village below. State is left blank on
        purpose — there is nothing in the line to derive it from, and guessing
        would print a wrong address on a government form.
      */
      const profilePincode = onlyDigits(
        applicationData?.appliedByUserPincode || joinedPincode
      ).slice(0, 6);
      const addressPincode =
        (addressLine.match(/\b[1-9]\d{5}\b/g) || []).pop() || "";

      /*
        The four address rows the profile fills, in the order the department
        expects a counter address to read — the firm the applicant walked into
        first, then the premises themselves, narrowing to the district:

          Flat / Door / Building     care-of firm     C/O LAKSHYA ENTERPRISES
          Road / Street / Block      address line     34N TESTER 34N TESTER
          Post Office                area             SHRI KRISHNA NAGAR
          Area / Locality / Town     city / village   MUSAKHEDI
          District                   district         INDORE

        They are laid out as a list and then poured into the rows, rather than
        each row reading one field, so that a profile missing any of them —
        most often the firm name — closes the gap instead of printing a blank
        row above a filled one. Only the first two rows are mandatory, so the
        last of the four is the one that drops off the end.
      */
      const areaLine = part(applicationData?.appliedByUserArea) || joined?.area || "";
      const districtLine =
        part(applicationData?.appliedByUserDistrict) || joinedDistrict || city;
      const addressLines = fitAddressLines([
        firm ? `C/O ${firm}` : "",
        addressLine,
        areaLine,
        // Skipped where it is standing in as the district, so the same name
        // does not print on two rows.
        districtLine === city ? "" : city,
      ]);

      counterPartsRef.current = {
        flat: addressLines[0] || "",
        road: addressLines[1] || "",
        postOffice: addressLines[2] || "",
        area: addressLines[3] || "",
        // District is a required box, so it falls back to the city / village
        // when the profile carries no district of its own.
        district: clampLine(districtLine),
        state: stateHit ? stateHit.name : "",
        stateCode: stateHit ? stateHit.code : "",
        pincode:
          profilePincode.length === 6
            ? profilePincode
            : addressPincode || profilePincode,
      };

    } else if (isPcoOfficeCategory) {
      /*
        PCO: the fixed head office, with the firm name on the first line.

        Nothing here comes off the application — the address is the same on
        every PCO form and only the firm rotates (see `pcoFirmFor`), so the
        operator has one line to check rather than eight to type.
      */
      const pcoLines = fitAddressLines([
        pcoFirmFor(
          applicationData?.srn || applicationData?._id || applicationData?.name
        ),
        PCO_OFFICE_ADDRESS.road,
        PCO_OFFICE_ADDRESS.postOffice,
        PCO_OFFICE_ADDRESS.area,
      ]);
      counterPartsRef.current = {
        flat: pcoLines[0] || "",
        road: pcoLines[1] || "",
        postOffice: pcoLines[2] || "",
        area: pcoLines[3] || "",
        district: clampLine(PCO_OFFICE_ADDRESS.district),
        state: PCO_OFFICE_ADDRESS.state,
        stateCode: PCO_OFFICE_ADDRESS.stateCode,
        pincode: PCO_OFFICE_ADDRESS.pincode,
      };
    }

    if (counterPartsRef.current) {
      const counter = counterPartsRef.current;

      if (isNew) {
        // Form 49A has its own office block, gated on "address for
        // communication" — the counter address goes there.
        counterAddress.officeFlat = counter.flat;
        counterAddress.officeRoad = counter.road;
        counterAddress.officePostOffice = counter.postOffice;
        counterAddress.officeArea = counter.area;
        counterAddress.officeDistrict = counter.district;
        counterAddress.officeState = counter.state;
        counterAddress.officeStateCode = counter.stateCode;
        counterAddress.officePincode = counter.pincode;
      } else {
        /*
          The correction form has ONE address block and a Residence / Office
          tick that says what kind of address it holds. A DSA or PCO
          correction opens on Office (below), so the block itself must hold
          that address — there is nowhere else for it to go, and leaving it
          blank is what made the Office tick look like it did nothing.
        */
        Object.assign(counterAddress, counter);
        counterPrefilledRef.current = true;
      }
    }

    setValues({
      ...emptyPanFormValues,
      firstName: name.first,
      middleName: name.middle,
      lastName: name.last,
      /*
        "Name as per Aadhaar" opens BLANK, and is mandatory.

        It used to be prefilled — first from the record's whole name, then from
        Middle + Last. Either way the operator was handed a value to accept
        rather than a box to fill, and a prefilled box is one nobody checks
        against the card in their hand. It is the one field on this form whose
        whole purpose is to say what the Aadhaar actually prints, so it is typed
        off the Aadhaar and then checked against the name above.
      */
      aadhaarName: "",
      /*
        Gender opens on "Select" — never on a guess.

        It used to be derived from the record's salutation (Mr. -> Male,
        Mrs. / Miss / Ms. -> Female). The PAN record carries no gender field, so
        that was the only signal available — but a salutation is a weak one, it
        is often left at the default when the application is filed, and it says
        nothing at all for a minor. A gender that disagrees with the applicant's
        Aadhaar is a rejection, and a box that is already filled is a box nobody
        re-reads.

        It is mandatory (`requiredKeys` above), so leaving it on Select is not a
        way past the form: Next: Preview stops on it and scrolls it into view.
        The operator picks it off the Aadhaar in front of them.
      */
      gender: "",
      dob: dobDigits,
      aadhaar: onlyDigits(applicationData?.adhaarNumber),
      pan: (applicationData?.panNumber || "").toUpperCase(),
      mobileCountryCode: "91",
      mobile: onlyDigits(applicationData?.mobileNumber).slice(-10),
      email: applicationData?.email || "",
      /*
        RA contact details (§21) and RA country (§20) — prefilled ONLY when the
        form actually opens on Representative Assessee, i.e. for a minor.

        They used to be prefilled on every application. The RA block is hidden
        in step 1 unless §22 is on RA, but the printed form has no such
        condition: §20 and §21 are laid down unconditionally
        (`raCountry` / `raMobileCC` / `raMobile` / `raEmail` in
        NEW_FORM_FIELDS), so an ordinary adult application printed a country,
        a mobile number and an e-mail into a Representative Assessee block
        whose every other line was blank — and named an RA the application does
        not have. Nothing on screen showed it, because the block the values
        belong to was not on screen.

        `isRaCommunication` is the same condition `addressForComm` uses just
        below, so the values exist exactly when the block that prints them does.
        All of it stays editable for the rare application where the RA is not
        the person who submitted it.
      */
      ...(isRaCommunication ? raContactFrom(applicationData) : {}),
      flat: "",
      road: "",
      postOffice: "",
      area: "",
      district: "",
      state: "",
      pincode: "",
      country: "INDIA",
      countryCode: "IN",
      fatherFirst: father.first,
      fatherMiddle: father.middle,
      fatherLast: father.last,
      motherFirst: mother.first,
      motherMiddle: mother.middle,
      motherLast: mother.last,
      // Q12 single parent: prefill from the record's boolean flag. New-style
      // applications always carry false, so this ticks "No" for them.
      singleParent: applicationData?.isSingleParent ? "YES" : "NO",
      // Q11 source of income: the record carries no source, so the NEW form
      // starts on "Income from Other Sources" — the common case for these
      // applications — and the operator can change it.
      incomeSources: isNew ? ["OTHER_SOURCES"] : [],
      // Parent-to-print — NEW form §15, correction form Q10. Prefer the
      // dedicated field; fall back to the legacy parentType for records created
      // before it existed.
      printParent:
        applicationData?.printParentType === "FATHER"
          ? "FATHER"
          : applicationData?.printParentType === "MOTHER"
          ? "MOTHER"
          : applicationData?.parentType === "FATHER"
          ? "FATHER"
          : applicationData?.parentType === "MOTHER"
          ? "MOTHER"
          : "",
      /*
        Correction address "select one". DSA and PCO applications are taken at
        a counter and the department writes to that counter, so they open on
        Office — filled in for DSA, empty for PCO. Same decision
        `addressForComm` makes for the NEW form below.
      */
      addressType: isOfficeAddressCategory ? "OFFICE" : "RESIDENCE",
      // NEW form section 7 — default to Resident (citizen-of-India form).
      residentialStatus: "RESIDENT",
      /*
        Office country — prefilled for DSA / PCO only.

        Those are the two categories whose form actually uses the Office
        Address block (the same flag sets the Office tick just above), so they
        are the two that want the country ready-filled. Everyone else files on
        their residence and leaves section 6 empty: filling the country there
        printed a blank office block with a lone INDIA in it, which reads at the
        counter as a form someone gave up on half way. Blank for them, and still
        editable in step 1 for the rare residence application that does carry an
        office address.
      */
      officeCountry: isOfficeAddressCategory ? "INDIA" : "",
      officeCountryCode: isOfficeAddressCategory ? "IN" : "",
      ...counterAddress,

      // ----- Page 2: Verification & Declaration prefills -----
      // Passport photograph for the form's photo box, prefilled from the
      // applicant's uploaded photo. Cropping it here does not touch that file.
      photo: applicationData?.passportPhotoUrl || "",
      // The stored `<ack>_Sig` first — the copy saved by an earlier PAN form
      // or by the affidavit, so both documents carry the same signature —
      // else the applicant's own upload. Still editable either way.
      signature:
        applicationData?.editedSignatureUrl ||
        applicationData?.signaturePhotoUrl ||
        "",
      declarantName: applicationData?.name || "",
      /*
        Capacity the form is verified in. A minor cannot verify their own
        application — it is filed and signed by the representative assessee —
        so a minor opens on RA (code 07) and everyone else on Self (code 01).

        The printed value is the short "RA" rather than the master's
        "REPRESENTATIVE ASSESSEE": the capacity blank on the declaration line is
        one narrow rule (11% of the page) with "do hereby declare" printed right
        after it, so the long form would be clipped mid-word. Same relationship
        the non-minor case already has between the printed "Self" and the
        dropdown's "SELF [ 01 ]" — the code is what reaches the vendor file.
      */
      declarantCapacity: isMinorApplicant ? "RA" : "Self",
      verifierCapacityCode: isMinorApplicant ? "07" : "01",
      /*
        Starts blank along with the address, then follows the city the operator
        types into whichever block is the address for communication (see
        `setField`) until they edit it themselves.

        Where a block opens already prefilled, Place opens on its district
        rather than staying blank until someone re-types a value that is already
        on screen: on a NEW form that is the OFFICE block, which a DSA or PCO
        application opens prefilled, and on a correction it is the single
        address block.

        A minor is the exception and opens BLANK: that form is filed by the
        representative assessee, so the place of verification is the RA's
        district, and the RA block is typed by the operator (only the RA's
        contact details are prefilled). Opening on the counter's district would
        put a place on the form that nothing on it verifies — and being
        prefilled, nobody would re-type it.
      */
      place: isRaCommunication
        ? ""
        : isNew
        ? ((counterAddress.officeDistrict as string) || "")
        : counterPrefilledRef.current
        ? counterPartsRef.current?.district || ""
        : "",
      // Printed at the foot of the form — kept the same as Date of Receipt (see
      // `receiptDate`). Still editable.
      date: receiptDate,
      // Printed at the foot of the NEW form, under "Designation:". It names the
      // same capacity as the declaration line above, so it follows it: RA for a
      // minor (the representative assessee signs), Self for everyone else.
      // Still editable for the rare application verified by someone else.
      designation: isMinorApplicant ? "RA" : "Self",
      /*
        NEW form §22 — communication address.

        A minor is filed through a representative assessee, so RA wins over
        everything else. Failing that, DSA and PCO applications are filed at a
        counter and default to Office (prefilled just above), and
        everything else to the residence.

        Only the NEW form prints this tick (the correction form uses
        `addressType`, which has no RA option), so a correction is left on
        RESIDENCE either way.
      */
      addressForComm: !isNew
        ? "RESIDENCE"
        : isMinorApplicant
        ? "RA"
        : isOfficeAddressCategory
        ? "OFFICE"
        : "RESIDENCE",

      // ----- Capture-only fields (vendor upload file, never printed) -----
      // Every application this panel handles is an individual PAN application
      // filed for a citizen of India; both stay editable.
      applicantStatus: "P",
      countryOfCitizenship: "IN",
      /*
        AO Code — looked up and stored at login (the verify dialog captures it
        where the remark used to be), so the operator does not have to find it
        again here. Still editable: a wrong code found at login is corrected on
        the form, not by re-doing the login.
      */
      aoAreaCode: (applicationData?.aoAreaCode || "").toUpperCase(),
      aoType: (applicationData?.aoType || "").toUpperCase(),
      aoRangeCode: `${applicationData?.aoRangeCode ?? ""}`,
      aoNo: `${applicationData?.aoNo ?? ""}`,
      acknowledgementNumber: applicationData?.acknowledgementNumber || "",
      physicalPanCard: "YES",
      hasOtherName: "NO",
      /*
        Both dates read the verification date, not the applied date — see
        `receiptDate` above. Each stays editable for the odd application
        received on a different day.
      */
      dateOfReceipt: receiptDate,
      dateOfVerification: asDate(applicationData?.verifiedOnDate) || today,
      photoPresent: applicationData?.passportPhotoUrl ? "YES" : "NO",
      signaturePresent: applicationData?.signaturePhotoUrl ? "YES" : "NO",
      // On by default; the operator can switch either off in step 1.
      crossSign:
        applicationData?.passportPhotoUrl && applicationData?.signaturePhotoUrl
          ? "YES"
          : "NO",
      receiptPage: applicationData?.acknowledgementPdf ? "YES" : "NO",

      // CORRECTION left "Tick Box" column — Sr. 1/3/7/8 checked by default
      // (Name / Date of Birth / Contact Details / Father's Name), the items
      // most commonly corrected. The operator can toggle any in step 1.
      changeName: true,
      changeDob: true,
      changeContact: true,
      changeFather: true,
      // CORRECTION Part C §11 "Documentary proof (other changes)" — ticked by
      // default; the operator can untick it in step 1.
      corrDocProof: true,
    });

    /**
     * Build the list of documents the applicant already uploaded, so the
     * operator can append any of them to the PDF as extra A4 pages.
     * Only entries that actually have a URL are listed.
     */
    // The passport photograph and the signature are deliberately NOT listed:
    // both are already printed into their own boxes on the form itself (from
    // `values.photo` / `values.signature`, prefilled from the very same
    // passportPhotoUrl / signaturePhotoUrl), so appending them again as extra
    // A4 pages only pads the PDF with a duplicate of what page 1 already shows.
    const candidates: Array<{ id: string; label: string; url?: string }> = [
      { id: "adhaarFrontPhotoUrl", label: "Aadhaar — Front", url: applicationData?.adhaarFrontPhotoUrl },
      { id: "adhaarBackPhotoUrl", label: "Aadhaar — Back", url: applicationData?.adhaarBackPhotoUrl },
      { id: "panFormFrontPhotoUrl", label: "PAN Form — Front", url: applicationData?.panFormFrontPhotoUrl },
      { id: "panFormBackPhotoUrl", label: "PAN Form — Back", url: applicationData?.panFormBackPhotoUrl },
      { id: "panCardFront", label: "PAN Card — Front", url: applicationData?.panCardFront },
      ...(applicationData?.dobProofDocuments || []).map((url: string, i: number) => ({
        id: `dobProof-${i}`,
        label: `DOB Proof ${i + 1}`,
        url,
      })),
      ...(applicationData?.otherDocuments || []).map((doc: any, i: number) => ({
        id: `other-${i}`,
        label: doc?.title || `Other Document ${i + 1}`,
        url: doc?.imageUrl,
      })),
      /*
        Files a staff member attached from the details page after verify
        (`uploadedFiles`). They belong in this list for the same reason the
        applicant's own scans do: a document that had to be chased up and
        attached later is usually the one the operator most needs to append.

        Deleted entries are out, and so are PDFs — an attachment is drawn as an
        <img> both on the card here and on the appended A4 page, and an <img>
        pointed at a PDF renders a broken icon rather than the document.
      */
      ...(applicationData?.uploadedFiles || [])
        .filter(
          (file: any) =>
            file && !file.isDeleted && file.fileUrl && file.mimeCategory !== "PDF"
        )
        .map((file: any, i: number) => ({
          // fileId is the server's uuid for the entry, so the key survives a
          // sibling being deleted; the index is only a fallback for older rows.
          id: `staffFile-${file?.fileId || i}`,
          label: file?.title || `Attached File ${i + 1}`,
          url: file?.fileUrl,
        })),
    ];
    /*
      REFILL: the documents the last generate appended, ticked again and laid
      out on the same pages. A crop made to a document then is not saved, so
      those come back as the original scan.
    */
    const savedAttachmentIds = new Set<string>(
      (isRefill && Array.isArray(savedFormData?.attachments)
        ? savedFormData?.attachments
        : []
      ).map((a: any) => a?.id)
    );
    const availableAttachments = candidates.filter((c) => !!c.url);
    setAttachments(
      availableAttachments.map((c) => ({
        id: c.id,
        label: c.label,
        url: c.url as string,
        selected: savedAttachmentIds.has(c.id),
      }))
    );
    if (isRefill && Array.isArray(savedFormData?.attachmentPages)) {
      const availableIds = new Set(availableAttachments.map((c) => c.id));
      setAttachmentPages(
        savedFormData?.attachmentPages
          .map((page: any) => ({
            id: page?.id,
            layout: page?.layout,
            items: (page?.items || []).filter((id: string) => availableIds.has(id)),
          }))
          .filter((page: any) => page.id && page.layout && page.items.length)
      );
    }

    /*
      REFILL: the saved values over the prefill just built. Only keys the form
      knows are taken, so a field added since the form was saved keeps its
      prefilled value and an old or derived key (incomeSourceCodes,
      attachments) never lands in the values.

      Photo and signature are not in formData. They come from the copies the
      operator cropped / cleaned last time (editedPhotoUrl /
      editedSignatureUrl), else the applicant's own upload as on a first fill.
      An edited signature is already ink-only, so the automatic clean-up pass
      is skipped for it: a second pass thins the strokes.
    */
    if (isRefill) {
      const restored: Partial<PanFormValues> = {};
      (Object.keys(emptyPanFormValues) as (keyof PanFormValues)[]).forEach((key) => {
        if (key === "photo" || key === "signature") return;
        /*
          Type switched in the Refill dialog: the applicant's details carry
          over, but the type-specific keys keep the new type's prefill, and an
          empty saved value does not blank a box the new type prefilled.
        */
        if (
          isTypeSwitched &&
          (TYPE_SPECIFIC_KEYS.has(key) ||
            savedFormData?.[key] === "" ||
            savedFormData?.[key] === null)
        ) {
          return;
        }
        if (savedFormData?.[key] !== undefined) {
          (restored as any)[key] = savedFormData[key];
        }
      });
      /*
        The office address's "C/O <firm>" row follows the agent's profile firm
        name as it is NOW, not as it was saved. The saved row kept an old firm
        name (e.g. one taken from the Employer Certificate Logo entry) on every
        refill — and the Employer Certificate copies its office address from
        this form, so it printed the old name too. Only that one row is
        replaced; the rest of what the operator saved stays as it was.
      */
      const counterFirmRow = counterPartsRef.current?.flat || "";
      if (counterFirmRow.startsWith("C/O ")) {
        const rows = (
          isNew
            ? ["officeFlat", "officeRoad", "officePostOffice", "officeArea"]
            : ["flat", "road", "postOffice", "area"]
        ) as (keyof PanFormValues)[];
        const firmRow = rows.find((key) =>
          String((restored as any)[key] || "").toUpperCase().startsWith("C/O ")
        );
        if (firmRow) (restored as any)[firmRow] = counterFirmRow;
      }
      const editedPhoto = applicationData?.editedPhotoUrl || "";
      const editedSignature = applicationData?.editedSignatureUrl || "";
      if (editedSignature) didCleanSignature.current = true;
      setValues((prev) => ({
        ...prev,
        ...restored,
        ...(editedPhoto ? { photo: editedPhoto } : {}),
        ...(editedSignature ? { signature: editedSignature } : {}),
      }));
    }

    setFormFillRemark(
      savedRemark ||
        applicationData?.formFillRemark ||
        (isRefill ? savedFormData?.formFillRemark : "") ||
        ""
    );

    setIsInitialized(true);
  }, [applicationData, isInitialized]);

  /**
   * Clean the signature PREFILLED from the applicant's uploaded file.
   *
   * That file is a photo of a signed slip, so its paper is some shade of grey —
   * and printed straight into the form's wide, short signature box it came out
   * as a grey rectangle with a small signature floating in the middle. The pass
   * drops the paper and trims to the strokes, so the signature fills the box.
   *
   * Runs once, only on a prefilled URL — a data-URL is already the output of the
   * crop tool, and re-cleaning it on every render would fight the operator's own
   * adjustments. If the pass can't improve the image it returns "" and the
   * original URL stays exactly as it was, still fixable by hand with the
   * "Ink only" tick and the brightness / contrast controls in Crop / Adjust.
   */
  const didCleanSignature = useRef(false);
  useEffect(() => {
    const src = values.signature;
    if (!isInitialized || didCleanSignature.current) return;
    if (!src || src.startsWith("data:")) return;
    // The stored copy was cleaned before it was saved; a second pass would
    // only thin the strokes.
    if (src === applicationData?.editedSignatureUrl) return;
    didCleanSignature.current = true;
    // No cancel-on-cleanup: under StrictMode the effect is run, cleaned up and
    // run again, and the second run stops at `didCleanSignature` — so a
    // cancelled first run meant the cleaned signature was never applied. The
    // result is applied only if the form still shows the image it was made
    // from, so a signature the operator changed meanwhile is not overwritten.
    cleanSignatureImage(src).then((cleaned) => {
      if (!cleaned) return;
      setValues((prev) =>
        prev.signature === src ? { ...prev, signature: cleaned } : prev
      );
    });
  }, [isInitialized, values.signature]);

  /**
   * Cut the receipt out of the acknowledgement PDF once, when the dialog opens.
   * A failure only drops the receipt page — it never blocks generating the form.
   */
  useEffect(() => {
    const url = applicationData?.acknowledgementPdf || "";
    if (!url) {
      setReceiptStatus("none");
      return;
    }
    let cancelled = false;
    setReceiptStatus("loading");
    extractReceiptImage(url)
      .then((image) => {
        if (cancelled) return;
        setReceiptImage(image);
        setReceiptStatus(image ? "ready" : "none");
      })
      .catch((error) => {
        console.error("Error cutting the receipt out of the acknowledgement:", error);
        if (!cancelled) setReceiptStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [applicationData?.acknowledgementPdf]);

  // ---------------------------------------------------------------------------
  // Attachments and the pages built from them
  // ---------------------------------------------------------------------------

  // A ref, not state: two page additions inside one batched render must not end
  // up with the same key.
  const newPageId = () => {
    pageSeq.current += 1;
    return `page-${pageSeq.current}`;
  };

  /**
   * Ticking a document gives it its own page straight away — that reproduces the
   * old one-document-per-page behaviour, and the operator only touches the page
   * builder when they actually want to merge documents. Unticking removes it
   * from every page and drops any page left empty.
   */
  const toggleAttachment = (id: string) => {
    const wasSelected = attachments.find((a) => a.id === id)?.selected;
    setAttachments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, selected: !a.selected } : a))
    );
    if (wasSelected) {
      setAttachmentPages((prev) =>
        prev
          .map((p) => ({ ...p, items: p.items.filter((i) => i !== id) }))
          .filter((p) => p.items.length > 0)
      );
    } else {
      setAttachmentPages((prev) => [
        ...prev,
        { id: newPageId(), layout: "1-up", items: [id] },
      ]);
    }
  };

  /*
    Cropping a document adjusts it. It does NOT attach it.

    It used to set `selected: true` as well, on the theory that nobody crops a
    document they are not going to append — but the operator also crops to READ
    a bad scan, and that quietly put a page into the PDF (and a page block below
    the cards) that they never ticked. Attach has exactly one control: the tick
    box.
  */
  const setAttachmentEdited = (id: string, dataUrl: string) =>
    setAttachments((prev) =>
      prev.map((a) => (a.id === id ? { ...a, edited: dataUrl || undefined } : a))
    );

  /**
   * Move an attached document onto another appended sheet — or onto a sheet of
   * its own (`target` === "new").
   *
   * Ticking Attach gives a document its own page, which is right for most of
   * them and wrong for the pair of small cards the operator wants side by side
   * on one sheet. Fixing that used to mean leaving the viewer, scrolling to the
   * page builder and moving the document through two slot dropdowns — so the
   * viewer now offers this directly, and this is what it calls.
   *
   * The sheet grows to fit: dropping a second document on a 1-up sheet makes it
   * 2-up rather than refusing. A sheet already holding four is full, and the
   * document goes onto a new sheet instead of silently doing nothing.
   */
  const moveAttachmentToPage = (id: string, target: string) => {
    setAttachmentPages((prev) => {
      const current = prev.find((p) => p.items.includes(id));
      if (current && current.id === target) return prev; // already there
      // Lift it off its old sheet. Slots become "" rather than closing up, so
      // the documents beside it keep the positions the operator gave them; a
      // sheet left holding nothing but holes goes.
      const without = prev
        .map((p) => ({ ...p, items: p.items.map((i) => (i === id ? "" : i)) }))
        .filter((p) => p.items.some(Boolean));

      const dest = target === "new" ? null : without.find((p) => p.id === target);
      if (!dest) {
        return [...without, { id: newPageId(), layout: "1-up" as AttachmentLayout, items: [id] }];
      }

      const items = [...dest.items];
      let layout = dest.layout;
      let slot = -1;
      for (let i = 0; i < slotsForLayout(layout); i++) {
        if (!items[i]) {
          slot = i;
          break;
        }
      }
      if (slot < 0) {
        // Full: step the sheet up to the next layout that has room for one more.
        const bigger = ATTACHMENT_LAYOUTS.find((l) => l.slots > slotsForLayout(layout));
        if (!bigger) {
          // Four already on the sheet — it takes no more, so this one gets its own.
          return [...without, { id: newPageId(), layout: "1-up" as AttachmentLayout, items: [id] }];
        }
        layout = bigger.key;
        slot = slotsForLayout(layout) - 1;
      }
      while (items.length <= slot) items.push("");
      items[slot] = id;
      return without.map((p) => (p.id === dest.id ? { ...p, layout, items } : p));
    });
  };

  const addPage = () =>
    setAttachmentPages((prev) => [
      ...prev,
      { id: newPageId(), layout: "2-up", items: [] },
    ]);

  const removePage = (pageId: string) =>
    setAttachmentPages((prev) => prev.filter((p) => p.id !== pageId));

  const setPageLayout = (pageId: string, layout: AttachmentLayout) =>
    setAttachmentPages((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? // Shrinking the layout drops the slots that no longer exist.
            { ...p, layout, items: p.items.slice(0, slotsForLayout(layout)) }
          : p
      )
    );

  /**
   * Put one document in one slot — and only there.
   *
   * A document is a single physical page, so it can occupy exactly one slot.
   * The dropdowns no longer OFFER one that is already placed
   * (`optionsForSlot` in PanFormStepOne), but the same document could still
   * reach two slots by being dragged onto a page, and an arrangement built
   * before that filter existed can already hold the duplicate — which printed
   * the same scan on two appended sheets. So the placement clears the document
   * out of every other slot rather than trusting the picker to be the only way
   * in.
   *
   * A slot vacated that way is left as "" instead of being closed up: slot
   * indices have to stay put under the labels the operator is reading.
   */
  const setPageSlot = (pageId: string, slot: number, attachmentId: string) =>
    setAttachmentPages((prev) => {
      const next = prev.map((p) => {
        // Empty slots are kept as "" placeholders so slot indices stay stable;
        // the template skips any id it can't resolve.
        const items = attachmentId
          ? p.items.map((i) => (i === attachmentId ? "" : i))
          : [...p.items];
        if (p.id !== pageId) return { ...p, items };
        while (items.length <= slot) items.push("");
        items[slot] = attachmentId;
        return { ...p, items };
      });
      /*
        A page the move just emptied goes with it.

        Taking a document off page 2 and onto page 1 left page 2 standing with
        one `— empty —` slot: an "Appended A4 pages (3 pages after the form)"
        count that no longer matched what would print, and a page the operator
        then had to Remove by hand.

        Only pages the move emptied are dropped. A page that was ALREADY empty
        was put there by the operator with "+ Add page" and is waiting to be
        filled — pulling it out from under them as they arrange another page
        would be its own bug. The page being filled is never dropped.
      */
      const hadSomething = new Map(
        prev.map((p) => [p.id, p.items.some(Boolean)])
      );
      return next.filter(
        (p) =>
          p.id === pageId || p.items.some(Boolean) || !hadSomething.get(p.id)
      );
    });

  // ---------------------------------------------------------------------------

  /**
   * "Place of Verifier" is the applicant's district in practice, so it is
   * derived from the address instead of being typed a second time: `district`
   * is the Town / City / District row, and `area` (Area / Locality / Town /
   * City) is only the fallback for an address that carries no district.
   *
   * The moment the operator types in Place themselves the value is theirs and
   * is never overwritten again.
   */
  const placeEditedRef = useRef(false);

  /**
   * "Name of Verifier" follows the Representative Assessee's name.
   *
   * A minor cannot verify their own application — the RA signs it — so the
   * verifier is the person whose name the operator types into REPRESENTATIVE
   * ASSESSEE DETAIL just above. That name is captured there as First / Middle /
   * Last and printed here as one line, so it is joined and carried across
   * rather than typed a second time (and mistyped, which is what put the
   * minor's own name on the declaration).
   *
   * Clearing the RA name again, or moving the address for communication off
   * RA — which wipes the RA block — puts the applicant's own name back, the
   * value the prefill opened on.
   *
   * As everywhere else in this dialog, the moment the operator types into Name
   * of Verifier the value is theirs and is never overwritten again.
   */
  const declarantNameEditedRef = useRef(false);
  const applicantName = applicationData?.name || "";


  /**
   * Date of Verification and Declaration Date follow Date of Receipt the same
   * way. The panel takes an application in hand, verifies it and signs the
   * declaration in one sitting, so all three carry the same day and retyping
   * it twice is only a chance to mistype it.
   *
   * Each stops following the moment the operator types into it — an
   * application genuinely verified or declared on a later day keeps the date
   * the operator gave it.
   */
  const verificationDateEditedRef = useRef(false);
  const declarationDateEditedRef = useRef(false);

  /**
   * Office and RA address blocks are shown only while they are the chosen
   * address for communication. Moving the choice elsewhere takes the block off
   * screen, so whatever was typed into it is wiped with it — otherwise an
   * address the operator can no longer see would still print on the form.
   */
  const clearedBlockFor = (choice: string): Partial<PanFormValues> => {
    const office = {
      officeFlat: "", officeRoad: "", officePostOffice: "", officeArea: "",
      officeDistrict: "", officeState: "", officeStateCode: "", officePincode: "",
    };
    const ra = {
      raFirst: "", raMiddle: "", raLast: "", raPan: "", raAadhaar: "",
      raFlat: "", raRoad: "", raPostOffice: "", raArea: "", raDistrict: "",
      raState: "", raStateCode: "", raPincode: "", raMobileCC: "", raMobile: "",
      raEmail: "", raLandlineStd: "", raLandline: "", raPoiCode: "", raPoaCode: "",
      // §20. Left out of this list once, and it printed INDIA into the country
      // rule of an RA block that had been emptied of everything else.
      raCountry: "", raCountryCode: "",
    };
    if (choice === "OFFICE") return ra;
    if (choice === "RA") return office;
    return { ...office, ...ra };
  };

  const ADDRESS_BLOCK_KEYS: (keyof CounterAddressParts)[] = [
    "flat",
    "road",
    "postOffice",
    "area",
    "district",
    "state",
    "stateCode",
    "pincode",
  ];

  const emptyAddressBlock: Partial<PanFormValues> = {
    flat: "", road: "", postOffice: "", area: "", district: "",
    state: "", stateCode: "", pincode: "",
  };

  /** Nothing typed into the correction form's address block yet. */
  const isAddressBlockEmpty = (v: PanFormValues) =>
    ADDRESS_BLOCK_KEYS.every((key) => !((v[key] as string) || "").trim());

  /** The block still holds exactly the counter address that was offered. */
  const holdsCounterAddress = (v: PanFormValues) => {
    const counter = counterPartsRef.current;
    if (!counter) return false;
    return ADDRESS_BLOCK_KEYS.every(
      (key) => ((v[key] as string) || "") === counter[key]
    );
  };

  const setField = (key: keyof PanFormValues, val: string) => {
    if (key === "place") placeEditedRef.current = true;
    if (key === "dateOfVerification") verificationDateEditedRef.current = true;
    if (key === "date") declarationDateEditedRef.current = true;
    if (key === "declarantName") declarantNameEditedRef.current = true;
    setValues((prev) => {
      const next = { ...prev, [key]: val };
      /*
        Place of Verifier is the district of the address the form is actually
        filed on — so it follows whichever block is the address for
        communication, not always the residence block.

        A DSA or PCO application is filed at the counter and opens on Office, so
        its Place is the OFFICE district. It used to read the residence block on
        those too, which is why a counter application printed the applicant's
        home district as the place of verification.

        A minor is filed and verified by the representative assessee, whose
        form opens on RA — so its Place is the RA block's district. Reading the
        applicant's block there printed the minor's own district as the place
        of verification, which is not where the form was verified at all.

        The correction form has only one address block, so there is nothing to
        choose between: it keeps reading `district` / `area`, and the Office
        tick decides what that block holds (see `addressType` below).
      */
      const placeKeys = !isNew
        ? (["district", "area"] as const)
        : next.addressForComm === "OFFICE"
        ? (["officeDistrict", "officeArea"] as const)
        : next.addressForComm === "RA"
        ? (["raDistrict", "raArea"] as const)
        : (["district", "area"] as const);
      if (!placeEditedRef.current && (placeKeys as readonly string[]).includes(key as string)) {
        next.place = (next[placeKeys[0]] || next[placeKeys[1]] || "").trim();
      }
      // Switching the address for communication changes WHICH district Place
      // should be reading, so it is recomputed off the block that just became
      // the live one rather than keeping the other block's district.
      if (!placeEditedRef.current && key === "addressForComm") {
        next.place = (next[placeKeys[0]] || next[placeKeys[1]] || "").trim();
      }
      if (
        !declarantNameEditedRef.current &&
        (key === "raFirst" || key === "raMiddle" || key === "raLast")
      ) {
        next.declarantName =
          joinName(next.raFirst, next.raMiddle, next.raLast) || applicantName;
      }
      if (key === "dateOfReceipt") {
        if (!verificationDateEditedRef.current) next.dateOfVerification = val;
        if (!declarationDateEditedRef.current) next.date = val;
      }
      if (key === "addressForComm" && val !== prev.addressForComm) {
        Object.assign(next, clearedBlockFor(val));
        // Moved ONTO Representative Assessee by hand — give the block the same
        // head start a minor's form opens with, rather than an empty §20/§21
        // the operator has to copy off the record themselves.
        if (val === "RA") Object.assign(next, raContactFrom(applicationData));
        // The RA block just went off screen and was wiped with it, so the name
        // it was feeding into the verifier line has to go back to the
        // applicant's own — otherwise the form keeps naming an RA that no
        // longer exists anywhere on it.
        if (!declarantNameEditedRef.current) {
          next.declarantName =
            joinName(next.raFirst, next.raMiddle, next.raLast) || applicantName;
        }
      }
      /*
        The correction form's one address block follows its Residence / Office
        tick. Ticking Office offers the counter address; ticking Residence takes
        it away again, because the retailer's premises are not the applicant's
        home. Only an untouched prefill is moved either way — the moment the
        operator edits a line the block is theirs and is left alone.
      */
      if (key === "addressType" && val !== prev.addressType && counterPartsRef.current) {
        const counter = counterPartsRef.current;
        if (val === "OFFICE" && isAddressBlockEmpty(prev)) {
          Object.assign(next, counter);
          counterPrefilledRef.current = true;
          if (!placeEditedRef.current) next.place = counter.district;
        } else if (val !== "OFFICE" && counterPrefilledRef.current && holdsCounterAddress(prev)) {
          Object.assign(next, emptyAddressBlock);
          counterPrefilledRef.current = false;
          if (!placeEditedRef.current) next.place = "";
        }
      }
      return next;
    });
  };

  const setFields = (patch: Partial<PanFormValues>) =>
    setValues((prev) => ({ ...prev, ...patch }));

  // Dragging the cross sign on the preview. Functional, because a drag fires
  // many moves between renders and each must add to the latest offset.
  const moveBy = (target: "crossSign" | "sig") => (dx: number, dy: number) =>
    setValues((prev) => {
      const at = (v: string) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0);
      const round = (n: number) => String(Math.round(n * 100) / 100);
      return {
        ...prev,
        [`${target}Dx`]: round(at(prev[`${target}Dx`]) + dx),
        [`${target}Dy`]: round(at(prev[`${target}Dy`]) + dy),
      };
    });
  const moveCrossSign = moveBy("crossSign");
  // The same for the signature in the Verification & Declaration box.
  const moveSig = moveBy("sig");

  // Toggle a boolean value (tick-box / proof / change-tick fields).
  const toggleBool = (key: keyof PanFormValues) =>
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleIncome = (key: IncomeSource) =>
    setValues((prev) => ({
      ...prev,
      incomeSources: prev.incomeSources.includes(key)
        ? prev.incomeSources.filter((k) => k !== key)
        : [...prev.incomeSources, key],
    }));

  /**
   * The label printed to the left of an input, so the toast can name the field
   * the operator has to go back to. Every row is `label cell | value cell`, so
   * the label is the previous sibling of the input or of one of its wrappers.
   */
  const labelOfField = (el: HTMLElement) => {
    let node: HTMLElement | null = el;
    for (let i = 0; i < 4 && node; i += 1) {
      const text = (node.previousElementSibling as HTMLElement | null)?.textContent?.trim();
      if (text) return text.replace(/\s*\*$/, "").trim();
      node = node.parentElement;
    }
    return "";
  };

  /**
   * Move the operator to the first mandatory field they left empty, in the
   * order the fields appear on screen (not the order of `requiredKeys`), and
   * return its label. Runs after the render that outlines the invalid fields.
   */
  const focusFirstMissing = (missing: string[]) =>
    new Promise<{ key: string; label: string }>((resolve) => {
      requestAnimationFrame(() => {
        const all = Array.from(
          document.querySelectorAll<HTMLElement>("[data-pan-field]")
        );
        const target = all.find((el) =>
          missing.includes(el.getAttribute("data-pan-field") || "")
        );
        if (!target) {
          resolve({ key: "", label: "" });
          return;
        }
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        // Radio / checkbox groups are plain divs carrying tabIndex={-1}, so
        // focusing them is safe too; preventScroll keeps the smooth scroll.
        target.focus?.({ preventScroll: true });
        resolve({
          key: target.getAttribute("data-pan-field") || "",
          label: labelOfField(target),
        });
      });
    });

  /** Mandatory check run when leaving step 1; returns true when everything is in. */
  const validate = async () => {
    const keys = requiredKeys(isNew, values.addressForComm);
    const isEmpty = (k: keyof PanFormValues) => {
      const v = values[k];
      return Array.isArray(v) ? v.length === 0 : !String(v ?? "").trim();
    };
    const missing = keys.filter(isEmpty);
    // Filled but short — a five-digit PIN reads as "done" to the empty check,
    // so the fixed-width fields are measured separately. A prefilled value can
    // also carry stray characters, hence the digits test rather than a length.
    const incomplete = keys.filter((k) => {
      const len = fixedLengthKeys[k];
      if (isEmpty(k) || len === undefined) return false;
      return !new RegExp(`^\\d{${len}}$`).test(String(values[k] ?? "").trim());
    });
    /*
      "Name as per Aadhaar" has to agree with the Full Name above it.

      The box is typed off the card in the operator's hand, and it is the one
      field whose whole job is to say what the Aadhaar prints — so a value that
      does not match the name being filed is caught here rather than at the
      counter. The comparison ignores case, spacing and punctuation
      (see `nameKey`); anything left that differs is a different name.

      Skipped when there is nothing to compare against — an empty box is already
      caught by the mandatory check above, and a Full Name with neither a middle
      nor a last box gives no target.
    */
    const accepted = acceptedAadhaarNames(
      values.firstName,
      values.middleName,
      values.lastName
    );
    const typedAadhaarName = String(values.aadhaarName ?? "").trim();
    const aadhaarNameMismatch =
      accepted.length > 0 &&
      !!typedAadhaarName &&
      !accepted.some((name) => nameKey(name) === nameKey(typedAadhaarName));
    const invalid = [
      ...missing,
      ...incomplete,
      ...(aadhaarNameMismatch ? (["aadhaarName"] as (keyof PanFormValues)[]) : []),
    ];
    setInvalidKeys(invalid as string[]);
    if (invalid.length === 0) return true;

    // Point at whichever problem field comes first on screen, and word the
    // message for that field: "still empty" is wrong for a short PIN.
    const { key, label } = await focusFirstMissing(invalid as string[]);
    const shortBy = fixedLengthKeys[key as keyof PanFormValues];
    showToast(
      "error",
      // Named separately: "still empty" would be wrong for a box that is filled,
      // and the operator needs to be told what it has to match.
      key === "aadhaarName" && aadhaarNameMismatch
        ? `"Name as per Aadhaar" must match the name above \u2014 ${accepted
            .map((name) => `"${name}"`)
            .join(" or ")}.`
        : !label
        ? "Please fill every mandatory field."
        : shortBy !== undefined && !missing.includes(key as keyof PanFormValues)
        ? `"${label}" must be exactly ${shortBy} digits.`
        : `Please fill "${label}" — ${invalid.length} mandatory field${
            invalid.length > 1 ? "s are" : " is"
          } still empty or incomplete.`
    );
    return false;
  };

  const downloadPdfFile = async (pdfUrl: string, downloadName?: string) => {
    try {
      const response = await fetch(pdfUrl, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch the PDF file.");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const segments = pdfUrl.split(/[\/?#]/);
      const fileName =
        downloadName || segments[segments.length - 1] || "pan-form.pdf";
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  /**
   * Employer Certificate, generated and downloaded on its own for a CAT_DSA
   * (175) or CAT_PCO (167) application right after the PAN form — the same rule the Employer
   * Certificate screen lists applications by, and the same template its
   * Download dialog prints (EmployerCertificateTemplate).
   *
   * The applicant block comes from the form values just submitted, so a
   * refilled form regenerates the certificate with the corrected details too.
   *
   * Never throws: a failure here only warns. The PAN form is already saved, and
   * the certificate can still be generated from the Employer Certificate screen.
   */
  /**
   * PCO (167) and DSA (175): the Employer Certificate is generated BEFORE the
   * PAN form and printed as the last page of the PAN form PDF, instead of
   * being downloaded on its own. (DSA used to be downloaded separately after
   * the form; it now goes inside it too, with the agent's seal & signature.)
   */
  const isEmbeddedCertificate = isEmployerCertificateCategory(
    applicationData?.baseCatAppied
  );

  /**
   * Build the Employer Certificate and save it (POST /employer-certificate/add).
   * Returns its HTML and file, or `{ error }` — never throws.
   */
  const createEmployerCertificate = async (
    formData: any
  ): Promise<
    | { html: string; fileUrl: string; ack?: string; error?: undefined }
    | { error: string | true }
  > => {
    const panId = applicationData?._id;
    if (!panId) return { error: true };
    try {
      const appRes: any = await fetchEmployerApplication(panId);
      const data = appRes?.data?.data;
      // The API's own reason (no permission, no data) over a generic message.
      if (!data) return { error: appRes?.error?.data?.message || true };
      // A failed firm lookup only drops the PCO issuer / seal, as before this
      // existed — never the certificate itself.
      let pcoFirms: any[] | undefined;
      try {
        const firmsRes: any = await fetchPcoFirms(undefined);
        pcoFirms = firmsRes?.data?.data;
      } catch {
        pcoFirms = undefined;
      }
      // DSA: the agent's signature — the saved one, or generated from their
      // profile name and saved now (the first certificate for this agent).
      let dsaSignatureImage: string | undefined;
      let dsaSealImage: string | undefined;
      if (isDsaCertificate(data)) {
        const signature = await resolveDsaSignature(data, (args) =>
          ensureDsaSignature(args)
        );
        // Seal and signature printed without their white background.
        const stamp = await cleanDsaStamp(data, signature.signatureImage);
        dsaSignatureImage = stamp.signatureImage;
        dsaSealImage = stamp.sealImage;
        if (signature.warning) {
          showToast("error", signature.warning);
          certificateWarnedRef.current = true;
        }
      }
      const html = employerCertificateHtml(
        data,
        formData,
        applicationType,
        values.photo,
        pcoFirms,
        dsaSignatureImage,
        dsaSealImage
      );
      const res: any = await addEmployerCertificate({ html, panId });
      const fileUrl = res?.data?.data?.employerertificateFile;
      if (res?.error || !fileUrl)
        return { error: res?.error?.data?.message || true };
      return { html, fileUrl, ack: data?.acknowledgementNumber };
    } catch {
      return { error: true };
    }
  };

  /**
   * AFFIDAVIT applications: the affidavit is generated with the PAN form — on
   * Fill and on Refill — from the values just submitted, saved as the
   * application's affidavit (POST /affidavit-certificate/add). Notary ON: printed
   * as the last page of the PAN form PDF (no separate download). Notary OFF:
   * plain, not in the PDF, downloaded on its own.
   */
  const isAffidavitApplication =
    applicationData?.dobProofType === "AFFIDAVIT (CATEGORY 03 ₹100)";

  /**
   * Build the affidavit and save it. Returns the page for the PAN form PDF
   * (`pageHtml`) and the saved file, or `{ error }` — never throws.
   */
  const createAffidavit = async (
    formData: any,
    settings: AffidavitStampSettings
  ): Promise<
    | { pageHtml: string; fileUrl: string; error?: undefined }
    | { error: string | true }
  > => {
    const panId = applicationData?._id;
    if (!panId) return { error: true };
    try {
      /*
        The serial number first: the application's own on a refill, else the
        next free one. It is the one call that can say no (no free number).
      */
      /*
        Notary OFF: no serial at all — not even one the application got
        earlier while Notary was ON (a refill used to print that old "NOTED &
        REGISTERED / Sr. No." on the plain affidavit).
      */
      let serial = null;
      if (isNotaryOn(settings)) {
        const serialRes: any = await reserveAffidavitSerial({ panId });
        if (serialRes?.error || serialRes?.data?.status === false) {
          return { error: serialRes?.error?.data?.message || serialRes?.data?.message || true };
        }
        serial = serialRes?.data?.data || null;
      }
      const [stamps, marks, signature] = await Promise.all([
        loadAffidavitStamps(settings),
        affidavitMarks(serial),
        // The signature the form itself prints, as a data-URL for the PDF.
        urlToDataUrl(values.signature || ""),
      ]);
      const fromForm = panFormToCertificate(formData, applicationType);
      const details = {
        applicantName: fromForm.applicantName || applicationData?.name || "",
        fatherName: fromForm.fatherName || applicationData?.parentName || "",
        dob: fromForm.dob || "",
        address: fromForm.affidavitAddress || "",
        place: fromForm.place || "",
      };
      // Same footer DATE as the Affidavit Certificate screen: the day it was applied.
      const applied = moment(applicationData?.appliedOnDate || applicationData?.createdAt);
      const props = {
        ...details,
        todayDate: (applied.isValid() ? applied : moment()).format("D MMMM YYYY"),
        signature,
        acknowledgementNumber: applicationData?.acknowledgementNumber,
        stamps,
        marks,
      };
      const res: any = await addAffidavitCertificate({
        html: affidavitHtml(props, "pdf"),
        panId,
        details,
      });
      const fileUrl = res?.data?.data?.affidavitertificateFile;
      if (res?.error || !fileUrl) return { error: res?.error?.data?.message || true };
      return { pageHtml: affidavitHtml(props, "image"), fileUrl };
    } catch {
      return { error: true };
    }
  };

  /** False only when a certificate was due and could not be generated. */
  const generateEmployerCertificate = async (formData: any): Promise<boolean> => {
    // PCO's / DSA's certificate was made before the form and is inside its PDF.
    if (isEmbeddedCertificate) return true;
    if (
      !applicationData?._id ||
      !isEmployerCertificateCategory(applicationData?.baseCatAppied)
    )
      return true;
    const result = await createEmployerCertificate(formData);
    if (result.error !== undefined) {
      showToast(
        "error",
        typeof result.error === "string"
          ? result.error
          : "PAN form saved, but the Employer Certificate could not be generated. Generate it from the Employer Certificate screen."
      );
      return false;
    }
    try {
      await downloadPdfFile(
        result.fileUrl,
        result.ack
          ? `${ackFileBaseName(result.ack, "Employer_Certificate")}.${fileExtensionFromUrl(result.fileUrl) || "pdf"}`
          : undefined
      );
    } catch {
      // The certificate is saved; it can be downloaded from its own screen.
    }
    return true;
  };

  /**
   * Save the photo and signature AS EDITED here (cropped / rotated / cleaned)
   * as `<ack>_Photo` / `<ack>_Sig`, on "Next: Preview".
   *
   * Only an edited image — a data-URL — is saved. A value that is still the
   * applicant's own URL means the form prints the original, so "" is sent to
   * clear any edited copy left by an earlier session. The applicant's
   * passportPhotoUrl / signaturePhotoUrl are never written to; the copies go
   * to their own fields (editedPhotoUrl / editedSignatureUrl).
   *
   * Fire-and-forget: the preview never waits on it, and a failure only warns —
   * it must not block generating the form.
   */
  const saveEditedImages = () => {
    if (!applicationData?._id) return;
    /*
      undefined = leave the stored copy alone. That is what a refilled form
      still showing last time's edited copy (its URL) means — sending "" for it
      would delete the very copy the form is printing.
    */
    const pick = (value: string, editedUrl?: string) =>
      value && value.startsWith("data:image/")
        ? value
        : value && editedUrl && value === editedUrl
        ? undefined
        : "";
    const next = {
      photo: pick(values.photo, applicationData?.editedPhotoUrl),
      signature: pick(values.signature, applicationData?.editedSignatureUrl),
    };
    const last = lastSavedImagesRef.current;
    if (last && last.photo === next.photo && last.signature === next.signature) {
      return;
    }
    // Still on the stored `<ack>_Sig` it opened with: leave that copy alone
    // (key omitted) instead of clearing it with "".
    const keepStoredSignature =
      !!applicationData?.editedSignatureUrl &&
      values.signature === applicationData.editedSignatureUrl;
    const { signature: nextSignature, ...nextRest } = next;
    savePanFormImages({
      panId: applicationData._id,
      ...nextRest,
      ...(keepStoredSignature ? {} : { signature: nextSignature }),
    }).then(
      (res: any) => {
        if (res?.error || !res?.data?.status) {
          showToast(
            "error",
            res?.error?.data?.message ||
              res?.data?.message ||
              "Could not save the edited photo / signature."
          );
        } else {
          lastSavedImagesRef.current = next;
        }
      }
    );
  };

  const generatePDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const input = document.getElementById("pdfContent") as HTMLElement;
    // Signature and photo are already baked into the posted HTML (embedded
    // <img>), so drop their (potentially large) base64 data-URLs from the
    // persisted formData. This keeps the stored record lean AND guarantees a
    // crop made here is never written back onto the application's own
    // signaturePhotoUrl / passportPhotoUrl.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { signature, photo, ...rest } = values;
    const formDataToPersist = {
      ...rest,
      /*
        The remark is ALSO carried inside formData, not only as its own
        top-level key on the request.

        The top-level key is the proper home for it: the API writes it onto the
        PAN application, where the details page reads it. But that write only
        exists in the newest API build, and an older one drops the key silently
        — which is exactly what happened on the dev server, where the admin
        posted the remark and nothing was stored. Every build of the API,
        current and older, saves `formData` onto the PanForm record wholesale,
        so a copy in here survives regardless of which API answers the call.
        `VIewPanCardApplication` prefers the application's own field and falls
        back to this one.

        It is inert on the form: the printed overlay renders the fields named in
        NEW_FORM_FIELDS / CORRECTION_FORM_FIELDS, never the keys of this object,
        so an extra key is not a field that can reach a government form.
      */
      formFillRemark: formFillRemark.trim(),
      // Source of income as the single-character master codes the vendor upload
      // file expects, alongside the keys the UI works with.
      incomeSourceCodes: values.incomeSources.map((k) => INCOME_SOURCE_CODES[k] || ""),
      // Record which documents were attached and how they were arranged
      // (identity only — never the image bytes). The originals are referenced
      // by their existing URLs.
      attachments: attachments
        .filter((a) => a.selected)
        .map(({ id, label, url }) => ({ id, label, url })),
      attachmentPages: attachmentPages.map(({ id, layout, items }) => ({
        id,
        layout,
        items,
      })),
    };
    /*
      PCO (167) / DSA (175): Employer Certificate first, then the PAN form with the
      certificate as its last page. No certificate, no form — a PCO form PDF
      without it would go out incomplete.
    */
    const extraPagesHtml: string[] = [];
    if (isEmbeddedCertificate) {
      const certificate = await createEmployerCertificate(formDataToPersist);
      if (certificate.error !== undefined) {
        showToast(
          "error",
          typeof certificate.error === "string"
            ? certificate.error
            : "The Employer Certificate could not be generated, so the PAN form was not generated. Try again."
        );
        setIsGenerating(false);
        return;
      }
      extraPagesHtml.push(certificate.html);
    }
    /*
      AFFIDAVIT applications: the affidavit next, printed after the certificate
      as the last page. No affidavit, no form — same rule as the certificate.
    */
    let affidavitFileUrl = "";
    /*
      Follows the Notary switch (Affidavit Stamp & Seal, client 5 Nov), on Fill
      and Refill:
      - ON: the complete affidavit (tickets, seals, serial) is made and printed
        as the last page of the PAN form PDF. NOT downloaded separately — it is
        already inside the PDF.
      - OFF: like before the Notary switch existed — the plain affidavit (no
        tickets, seals or serial) is made, saved, printed as the last page of
        the PAN form PDF AND downloaded as its own file (client, 5 Nov). If it
        fails, the form is still generated without it and the operator is told
        to make it from the Affidavit Certificate screen.
    */
    let notaryOn = false;
    let plainAffidavitFailed = false;
    const stampSettings: AffidavitStampSettings | null = isAffidavitApplication
      ? ((await fetchAffidavitStamps(undefined)) as any)?.data?.data || null
      : null;
    if (isAffidavitApplication && !stampSettings) {
      showToast("error", "Could not read the affidavit settings, so the PAN form was not generated. Try again.");
      setIsGenerating(false);
      return;
    }
    if (stampSettings && !isNotaryOn(stampSettings)) {
      const affidavit = await createAffidavit(formDataToPersist, stampSettings);
      if (affidavit.error !== undefined) {
        plainAffidavitFailed = true;
      } else {
        // Last page of the PAN form PDF, as before.
        extraPagesHtml.push(affidavit.pageHtml);
        affidavitFileUrl = affidavit.fileUrl;
      }
    }
    if (stampSettings && isNotaryOn(stampSettings)) {
      notaryOn = true;
      const affidavit = await createAffidavit(formDataToPersist, stampSettings);
      if (affidavit.error !== undefined) {
        showToast(
          "error",
          typeof affidavit.error === "string"
            ? affidavit.error
            : "The affidavit could not be generated, so the PAN form was not generated. Try again."
        );
        setIsGenerating(false);
        return;
      }
      // Inside the PDF only; affidavitFileUrl stays empty, so no separate download.
      extraPagesHtml.push(affidavit.pageHtml);
    }
    addPanForm({
      html: input?.outerHTML,
      ...(extraPagesHtml.length ? { extraPagesHtml } : {}),
      panId: applicationData?._id,
      applicationType,
      // Persist the filled field values so they can be reused later
      // (re-generation, reference, the vendor upload file).
      formData: formDataToPersist,
      // Optional, and deliberately outside formData: this is a workflow note
      // read on the PAN Details page, not a filled field. Trimmed so a stray
      // space does not put an empty red banner on the application.
      formFillRemark: formFillRemark.trim(),
      // Audit only: the API stamps lastRefilledAt / By and logs PANFORM_REFILL.
      ...(isRefill ? { isRefill: true } : {}),
    })
      .then(async (res: any) => {
        if (res.error) {
          showToast("error", res?.error?.data?.message);
          setIsGenerating(false);
        } else {
          const fileUrl = res?.data?.data?.panFormFile;
          if (fileUrl) {
            // Awaited, not fired and forgotten: onGenerated may close this tab,
            // and a fetch still in flight would be cancelled along with it.
            await downloadPdfFile(fileUrl);
            showToast("success", res?.data?.message);
            // Awaited for the same reason: it downloads a second PDF.
            const employerOk = await generateEmployerCertificate(
              formDataToPersist
            );
            // Notary OFF: the plain affidavit made with it, as its own file.
            // A refill regenerated it above, so it never keeps the old details.
            // (Notary ON: it is inside the PAN form PDF — no separate file.)
            if (affidavitFileUrl && !notaryOn) {
              try {
                await downloadPdfFile(
                  affidavitFileUrl,
                  applicationData?.acknowledgementNumber
                    ? `${ackFileBaseName(applicationData.acknowledgementNumber, "Affidavit")}.${fileExtensionFromUrl(affidavitFileUrl) || "pdf"}`
                    : undefined
                );
              } catch {
                // Saved; it can be downloaded from the Affidavit Certificate screen.
              }
            }
            if (plainAffidavitFailed) {
              showToast(
                "error",
                "PAN form generated, but the affidavit could not be made. Generate it from the Affidavit Certificate screen."
              );
            }
            // onGenerated closes this window; give a warning time to be read
            // first, or it vanishes with the window.
            if (!employerOk || certificateWarnedRef.current || plainAffidavitFailed) {
              await new Promise((resolve) => setTimeout(resolve, 3500));
            }
            onGenerated?.(fileUrl);
            onClose();
          } else {
            setIsGenerating(false);
          }
        }
      })
      .catch(() => setIsGenerating(false));
  };

  const stepChip = (n: 1 | 2, title: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div
        style={{
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          fontWeight: 700,
          color: step === n ? "#fff" : "#555",
          background: step === n ? "#333" : "#e0e0e0",
        }}
      >
        {n}
      </div>
      <span
        style={{
          fontSize: "13px",
          fontWeight: step === n ? 700 : 400,
          color: "#000",
        }}
      >
        {title}
      </span>
    </div>
  );

  return (
    <>
      {/*
        Step indicator on the left, the form's actions on the right — one bar
        pinned to the top of the page.

        The actions used to sit at the foot of the form. On a two-page
        government form that put Close and Next below everything, so finishing
        one meant scrolling back down past the whole form to reach them; up here
        they are on screen from the moment the page opens and never move.

        `-mx-6 px-6` bleeds the bar out through the page's gutter so its rule
        and white background run the full width of the content column, while the
        chips and buttons stay on the gutter — the same left and right edge as
        the form beneath.

        Its height is fixed rather than left to the buttons, because the photo /
        signature strip inside the form pins itself directly below this bar and
        needs a number to offset against (ACTION_BAR_HEIGHT).
      */}
      <div
        className="sticky top-0 z-50 -mx-6 px-6 flex items-center gap-[14px] bg-white border-b"
        style={{ height: `${ACTION_BAR_HEIGHT}px` }}
      >
        {stepChip(1, "Fill details")}
        <div style={{ flex: "0 0 40px", height: "1px", background: "#ccc" }} />
        {stepChip(2, "Preview & generate")}

        {/*
          Remark — optional, and it takes the space the spacer used to hold, so
          the bar keeps its one-row shape and the actions stay where they were.

          Red like the banner it becomes on the PAN Details page, so the two
          read as the same thing seen from either side of the hand-off. It is on
          both steps rather than step 1 alone: an operator who only notices
          something worth saying while checking the preview would otherwise have
          to go Back to say it.
        */}
        <label className="grow flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-red-600 shrink-0">
            Remark :
          </span>
          <input
            type="text"
            value={formFillRemark}
            onChange={(e) => setFormFillRemark(e.target.value)}
            placeholder="Remark yaha type kare"
            maxLength={500}
            className="grow min-w-0 border border-red-500 rounded px-2 py-[6px] text-sm text-red-600 placeholder:text-slate-400 outline-none focus:border-red-600"
          />
        </label>

        <ATMLoadingButton
          className="border-primary-main text-primary-main bg-white"
          onClick={onClose}
        >
          Close
        </ATMLoadingButton>

        {step === 2 && (
          <ATMLoadingButton
            className="border-primary-main text-primary-main bg-white"
            onClick={() => setStep(1)}
          >
            <div className="font-medium flex items-center">
              <span>Back</span>
            </div>
          </ATMLoadingButton>
        )}

        {step === 1 ? (
          <ATMLoadingButton
            className="bg-primary-main"
            onClick={async () => {
              if (await validate()) {
                saveEditedImages();
                setStep(2);
              }
            }}
          >
            <div className="font-medium flex items-center">
              <span>Next: Preview</span>
            </div>
          </ATMLoadingButton>
        ) : (
          <ATMLoadingButton
            type="submit"
            onClick={() => generatePDF()}
            className="bg-primary-main"
            loadingText="Generating..."
            isLoading={isGenerating}
            disabled={isGenerating}
          >
            <div className="font-medium flex items-center">
              <span>Submit</span>
            </div>
          </ATMLoadingButton>
        )}
      </div>

      {step === 1 && (
        <PanFormStepOne
          applicationType={applicationType}
          values={values}
          setField={setField}
          setFields={setFields}
          toggleBool={toggleBool}
          toggleIncome={toggleIncome}
          attachments={attachments}
          attachmentPages={attachmentPages}
          onToggleAttachment={toggleAttachment}
          onAttachmentEdited={setAttachmentEdited}
          onMoveAttachmentToPage={moveAttachmentToPage}
          onAddPage={addPage}
          onRemovePage={removePage}
          onSetPageLayout={setPageLayout}
          onSetPageSlot={setPageSlot}
          invalidKeys={invalidKeys}
          receiptImage={receiptImage}
          receiptStatus={receiptStatus}
          originalSignature={applicationData?.signaturePhotoUrl || ""}
        />
      )}

      {/* Step 2 — A4 preview of exactly what will print. Kept mounted only on
          this step; generatePDF() reads #pdfContent from it. */}
      {/* Adjusted here as well as in step 1: this is where the result is
          visible, so the nudge and its effect are on one screen. Pinned under
          the action bar so it stays in reach while scrolled down to page 1, and
          outside #pdfContent, so it never prints. */}
      {step === 2 && values.signature && (
        <div
          className="sticky z-40 -mx-6 px-6 flex flex-wrap items-center gap-x-6 gap-y-1 bg-white border-b py-2"
          style={{ top: `${ACTION_BAR_HEIGHT}px` }}
        >
          {/* The two controls side by side on one row, so the bar takes half
              the height above the preview. It still wraps to two rows on a
              screen too narrow for both. */}
          {values.crossSign === "YES" && (
            <div className="flex items-center flex-wrap gap-3 pr-6 border-r">
              <span className="text-sm font-medium">Cross sign on left photo:</span>
              <CrossSignControls values={values} setFields={setFields} />
            </div>
          )}
          <div className="flex items-center flex-wrap gap-3">
            <span className="text-sm font-medium">Signature in declaration box:</span>
            <CrossSignControls values={values} setFields={setFields} target="sig" />
          </div>
          <span className="text-xs text-slate-500">
            Or drag either signature on the form.
          </span>
        </div>
      )}
      {step === 2 && (
        <div style={{ overflow: "auto", background: "#e5e5e5", padding: "12px 0" }}>
          <PanFormTemplate
            applicationType={applicationType}
            values={values}
            attachments={attachments}
            attachmentPages={attachmentPages}
            receiptImage={receiptImage}
            onCrossSignMove={moveCrossSign}
            onSigMove={moveSig}
          />
        </div>
      )}

    </>
  );
};

export default PanFormDialog;
